import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';
import "./RecruiterDashboard.css";
import { getRecruitmentDashboard } from "./Service/RecruitmentService";
import { TostMsg } from '../../../../App';

/* ---------- helpers ---------- */
const dash = (v) => (v === null || v === undefined || v === "" ? "-" : v);
const arr = (v) => (Array.isArray(v) ? v : []);
const initials = (name) =>
  name
    ? name.trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase()
    : "-";

const splitTime = (raw) => {
  if (!raw) return ["-", ""];
  const d = new Date(raw);
  if (!isNaN(d.getTime())) {
    const [t, ap] = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }).split(" ");
    return [t, ap || ""];
  }
  const m = String(raw).match(/(\d{1,2}:\d{2})\s*(AM|PM)?/i);
  return m ? [m[1], (m[2] || "").toUpperCase()] : [String(raw), ""];
};

const STRIPES = ["int-stripe-primary", "int-stripe-blue", "int-stripe-green"];
const AVATARS = ["avatar-red", "avatar-blue", "avatar-purple", "avatar-green"];
const FUNNEL_ROWS = [
  ["Applied", "funnel-applied"],
  ["Screening", "funnel-screening"],
  ["Interview", "funnel-interview"],
  ["Shortlisted", "funnel-shortlisted"],
  ["Offer Given", "funnel-offer"],
  ["Hired", "funnel-hired"],
];
const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function RecruitmentDashboard() {
  const [showModals, setShowModals] = useState({ feedback: false });
  const [ratings, setRatings] = useState({ r1: 0, r2: 0, r3: 0, r4: 0 });
  const [selectedInterview, setSelectedInterview] = useState(null);

  const openModal = (modal) => setShowModals((prev) => ({ ...prev, [modal]: true }));
  const closeModal = (modal) => setShowModals((prev) => ({ ...prev, [modal]: false }));

  const toast = (msg, type = "success") => {
    const t = document.createElement("div");
    t.className = `og-toast ${type === "warn" ? "og-toast-warn" : type === "error" ? "og-toast-error" : "og-toast-success"}`;
    const icon =
      type === "warn" ? "fa-triangle-exclamation" : type === "error" ? "fa-times-circle" : "fa-check-circle";
    t.innerHTML = ` <i class="fa-solid ${icon}"></i> <span>${msg}</span> `;
    document.body.appendChild(t);
    setTimeout(() => {
      t.classList.add("og-toast-hide");
      setTimeout(() => t.remove(), 300);
    }, 2800);
  };
  const setRating = (id, value) => setRatings((prev) => ({ ...prev, [id]: value }));

  /* ---------- API data ---------- */
  const [dashboard, setDashboard] = useState({});
  const toster = useContext(TostMsg);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const params = { ViewMode: 2 };
      const response = await getRecruitmentDashboard(params);
      setDashboard(response?.Data || {});
    } catch (error) {
      toster?.current?.error('Failed to load Dashboard');
      setDashboard({});
    } finally {
      setLoading(false);
    }
  };

  /* ---------- derived data ---------- */
  const kpis = dashboard?.KPIs || {};
  const todayInterviews = arr(dashboard?.TodayInterviews);
  const activeJobs = arr(dashboard?.ActiveJobs);
  const pendingActions = arr(dashboard?.PendingActions);
  const funnel = arr(dashboard?.PipelineFunnel);
  const offers = arr(dashboard?.OffersInFlight);
  const upcoming = arr(dashboard?.UpcomingInterviews);
  const month = dashboard?.MonthStats || {};

  const overdueFeedbackCount = pendingActions.filter((a) => a.ActionType === "OverdueFeedback").length;
  const nextInterviewTime = todayInterviews.length
    ? splitTime(todayInterviews[0].InterviewTime || todayInterviews[0].StartTime || todayInterviews[0].ScheduledAt).join(" ").trim()
    : null;

  const funnelCount = (stage) => {
    const row = funnel.find((f) => f.Stage === stage);
    return row ? row.CandidateCount : null;
  };
  const funnelMax = Math.max(0, ...funnel.map((f) => f.CandidateCount || 0));
  const offerHiredTotal = funnel.length
    ? ["Offer Given", "Offer Accepted", "Hired"].reduce((s, st) => s + (funnelCount(st) || 0), 0)
    : null;

  const todayIdx = new Date().getDay();
  const weekCounts = DAY_LABELS.map((_, i) =>
    upcoming.filter((u) => {
      const d = new Date(u.InterviewDate || u.ScheduledAt || u.StartTime);
      return !isNaN(d.getTime()) && d.getDay() === i;
    }).length
  );
  const weekDays = [1, 2, 3, 4, 5, 6]; // Mon - Sat

  const todayText = new Date().toLocaleDateString("en-US", {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
  });

  const actionMeta = (type) => {
    switch (type) {
      case "OverdueFeedback":
        return { icon: "fa-star", cls: "action-icon-amber", title: "Feedback overdue", link: "/Recruitment/Reimbursement-Interview", btn: "Review" };
      case "OfferAwaitingResponse":
        return { icon: "fa-file-contract", cls: "action-icon-green", title: "Offer pending acceptance", link: "/Recruitment/Candidate", btn: "View" };
      default:
        return { icon: "fa-circle-exclamation", cls: "action-icon-blue", title: dash(type), link: "/Recruitment/Candidate", btn: "View" };
    }
  };

  const EmptyDash = () => <div className="muted-text" style={{ padding: ".75rem 1rem" }}>-</div>;

  return (
    <>
      <div className="topbar">
        <i className="fa-solid fa-gauge topbar-icon"></i>
        <strong className="topbar-title">Recruitment Dashboard</strong>
        <div className="topbar-date">{todayText}</div>

        <div className="top-right">
          <select className="og-select topbar-select">
            <option>My Assignments</option>
            <option>All Recruiters</option>
            <option>Team Overview</option>
          </select>

          <div className="top-user">
            <div className="top-avatar">AM</div>
            <div>
              <div className="top-user-name">Arun Mishra</div>
              <div className="top-user-role">HR Admin · Recruiter</div>
            </div>
          </div>
        </div>
      </div>

      <div className="container-fluid position-relation">
        <div className="greeting">
          <div className="greeting-text">
            <h2>Good morning, Arun 👋</h2>
            <p>
              You have <strong className="greeting-interviews">{dash(kpis.TodayInterviews)} interviews</strong> today
              and <strong className="greeting-feedback">{dash(kpis.FeedbackPending)} feedback</strong> submissions
              pending. Here's your recruitment snapshot.
            </p>
          </div>

          <div className="greeting-actions">
            <Link className="btn btn-sm greeting-btn-light" to="/Recruitment/Candidate-CVUpload">
              <i className="fa-solid fa-cloud-arrow-up"></i>
              Upload CV
            </Link>
            <Link className="btn btn-sm greeting-btn-primary" to="/Recruitment/ScheduleInterview">
              <i className="fa-solid fa-calendar-plus"></i>
              Schedule Interview
            </Link>
            <Link className="btn btn-sm greeting-btn-light" to="/Recruitment/Reimbursement-Job/Create">
              <i className="fa-solid fa-plus"></i>
              Create Job
            </Link>
          </div>
        </div>

        <div className="kpi-strip">
          <Link to="/Recruitment/JobsPage">
            <div className="kpi">
              <div className="kpi-val kpi-primary">{dash(kpis.OpenJobs)}</div>
              <div className="kpi-lbl">Open Jobs</div>
              <div className="kpi-trend kpi-trend-muted">-</div>
            </div>
          </Link>

          <Link className="kpi" to="/Recruitment/Candidate">
            <div className="kpi-val kpi-blue">{dash(kpis.ActiveCandidates)}</div>
            <div className="kpi-lbl">Total Candidates</div>
            <div className="kpi-trend kpi-trend-muted">-</div>
          </Link>

          <Link to="/Recruitment/Reimbursement-Interview" className="kpi">
            <div className="kpi-val kpi-amber">{dash(kpis.TodayInterviews)}</div>
            <div className="kpi-lbl">Today's Interviews</div>
            <div className="kpi-trend kpi-trend-muted">
              {nextInterviewTime ? `Next: ${nextInterviewTime}` : "-"}
            </div>
          </Link>

          <Link className="kpi" to="/Recruitment/Reimbursement-Interview">
            <div className="kpi-val kpi-red">{dash(kpis.FeedbackPending)}</div>
            <div className="kpi-lbl">Feedback Pending</div>
            <div className="kpi-trend kpi-trend-red">
              {pendingActions.length ? (
                <>
                  <i className="fa-solid fa-triangle-exclamation "></i>
                  {overdueFeedbackCount} overdue
                </>
              ) : "-"}
            </div>
          </Link>

          <div className="kpi">
            <div className="kpi-val kpi-purple">{dash(kpis.OffersPending)}</div>
            <div className="kpi-lbl">Offers Pending</div>
            <div className="kpi-trend kpi-trend-muted">
              {kpis.OffersPending ? "Awaiting acceptance" : "-"}
            </div>
          </div>

          <Link to="/Recruitment/Talent-Requests-Page" className="kpi">
            <div className="kpi-val kpi-gray">{dash(kpis.TRApprovalsNeeded)}</div>
            <div className="kpi-lbl">TR Approvals</div>
            <div className="kpi-trend kpi-trend-amber">
              {kpis.TRApprovalsNeeded ? (
                <>
                  <i className="fa-solid fa-clock "></i>
                  Needs review
                </>
              ) : "-"}
            </div>
          </Link>
        </div>

        <div className="dashboard-grid">
          {/* Today's Interviews */}
          <div className="card">
            <div className="card-header">
              <h4 className="card-header-title">
                <i className="fa-solid fa-circle primary-icon"></i>
                Today's Interviews
              </h4>
              <Link to="/Recruitment/ScheduleInterview" className="btn btn-primary btn-xs">
                <i className="fa-solid fa-plus"></i>
                Schedule
              </Link>
            </div>

            {todayInterviews.length === 0 ? (
              <EmptyDash />
            ) : (
              todayInterviews.map((it, i) => {
                const [hour, ampm] = splitTime(it.InterviewTime || it.StartTime || it.ScheduledAt);
                const name = it.CandidateName;
                const panel = arr(it.Panelists || it.PanelMembers || it.Interviewers);
                const needsFeedback = !it.FeedbackSubmitted && !it.IsFeedbackSubmitted;
                return (
                  <div className="interview-card" key={it.InterviewId || it.ReferenceId || i}>
                    <div className="int-time">
                      <div className="int-hour">{hour}</div>
                      <div className="int-ampm">{ampm}</div>
                    </div>

                    <div className={`int-stripe ${STRIPES[i % STRIPES.length]}`}></div>

                    <div className="int-info">
                      <Link to="/Recruitment/Candidate-Profile" className="int-name">
                        {dash(name)}
                      </Link>
                      <div className="int-meta">
                        <span>{dash(it.RoundName)}</span>
                        <span>
                          <i className="fa-solid fa-video"></i> {dash(it.InterviewMode || it.Mode)}
                        </span>
                      </div>
                      <div className="int-meta int-meta-job">
                        <span className="muted-text">{dash(it.JobCode)}</span>
                      </div>
                    </div>

                    <div className="interview-actions">
                      <div className="avatar-group">
                        {panel.length === 0 ? (
                          <div className={`panel-av ${AVATARS[0]}`}>-</div>
                        ) : (
                          panel.slice(0, 3).map((p, pi) => (
                            <div className={`panel-av ${AVATARS[pi % AVATARS.length]}`} key={pi}>
                              {initials(typeof p === "string" ? p : p?.Name || p?.FullName)}
                            </div>
                          ))
                        )}
                      </div>

                      {needsFeedback ? (
                        <button
                          className="btn btn-primary btn-xs"
                          onClick={() => {
                            setSelectedInterview(it);
                            openModal("feedback");
                          }}
                        >
                          <i className="fa-solid fa-star"></i>
                          Feedback
                        </button>
                      ) : (
                        <span className="int-badge badge-open">{dash(it.Status)}</span>
                      )}
                    </div>
                  </div>
                );
              })
            )}

            <div className="card-footer">
              <Link to="/Recruitment/Reimbursement-Interview" className="view-link">
                View all interviews →
              </Link>
            </div>
          </div>

          {/* My Active Jobs */}
          <div className="card">
            <div className="card-header">
              <h4 className="card-header-title simple">
                <i className="fa-solid fa-briefcase briefcase-icon"></i>
                My Active Jobs
              </h4>
              <Link to="/Recruitment/JobsPage" className="btn btn-outline btn-xs">
                All Jobs
              </Link>
            </div>

            {activeJobs.length === 0 ? (
              <EmptyDash />
            ) : (
              activeJobs.map((job) => {
                const segs = [
                  ["pm-applied", "Screening", job.InScreening],
                  ["pm-interview", "Interview", job.InInterview],
                  ["pm-shortlisted", "Shortlisted", job.Shortlisted],
                  ["pm-offer", "Offer/Hired", job.OfferedOrHired],
                ];
                const isOpen = job.JobStatus === "Open";
                return (
                  <Link to="/Recruitment/Job-Detail-Recruitment" className="job-row" key={job.JobId || job.JobCode}>
                    <div className="jr-left">
                      <div className="jr-title">{dash(job.Title)}</div>
                      <div className="jr-sub">
                        {dash(job.JobCode)} ·{" "}
                        <span className={job.JobAgeInDays > 30 ? "open-days-amber" : "open-days"}>
                          {job.JobAgeInDays !== undefined && job.JobAgeInDays !== null ? `${job.JobAgeInDays}d open` : "-"}
                        </span>
                      </div>

                      <div className="pipeline">
                        {segs.map(([cls, label, count]) =>
                          count > 0 ? (
                            <div
                              key={cls}
                              className={`pm-seg ${cls}`}
                              style={{ width: `${count * 6}px` }}
                              title={`${label}: ${count}`}
                            ></div>
                          ) : null
                        )}
                        <span className="pipeline-count">{dash(job.TotalCandidates)} total</span>
                      </div>
                    </div>

                    <span className={`badge ${isOpen ? "badge-open" : "badge-hold"}`}>{dash(job.JobStatus)}</span>
                  </Link>
                );
              })
            )}

            <div className="card-footer">
              <Link to="/Recruitment/JobsPage" className="view-link">
                View all {dash(kpis.OpenJobs)} open jobs →
              </Link>
            </div>
          </div>

          {/* Pending Actions */}
          <div className="card">
            <div className="action-header">
              <h4 className="card-header-title simple">
                <i className="fa-solid fa-circle-exclamation pending-icon"></i>
                Pending Actions
              </h4>
            </div>

            {pendingActions.length === 0 ? (
              <EmptyDash />
            ) : (
              pendingActions.map((a, i) => {
                const meta = actionMeta(a.ActionType);
                const isOffer = a.ActionType === "OfferAwaitingResponse";
                return (
                  <div className="action-item" key={`${a.ActionType}-${a.ReferenceId}-${i}`}>
                    <div className={`ai-icon ${meta.cls}`}>
                      <i className={`fa-solid ${meta.icon}`}></i>
                    </div>

                    <div className="ai-text">
                      <div className="ai-title">
                        {meta.title} — {dash(a.CandidateName)}
                      </div>
                      <div className="ai-sub">
                        {dash(a.JobTitle)} · {dash(a.RoundName)} ·{" "}
                        {a.DaysOverdue !== undefined && a.DaysOverdue !== null ? `${a.DaysOverdue}d overdue` : "-"}
                      </div>
                    </div>

                    {isOffer ? (
                      <button className="btn btn-outline btn-xs" title="Send reminder">
                        <i className="fa-solid fa-bell"></i>
                      </button>
                    ) : (
                      <Link to={meta.link} className="btn btn-outline btn-xs">
                        {meta.btn}
                      </Link>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div className="pipeline-section">
          {/* Pipeline Summary */}
          <div className="card">
            <div className="card-header">
              <h4>
                <i className="fa-solid fa-pipe-section pipeline-icon"></i>
                Pipeline Summary
              </h4>

              <select className="og-select pipeline-select">
                <option>All my jobs</option>
                {activeJobs.map((j) => (
                  <option key={j.JobId || j.JobCode}>{j.JobCode}</option>
                ))}
              </select>
            </div>

            <div className="pipeline-body">
              <div className="pipeline-stats">
                <div className="pipeline-stat stat-applied">
                  <div className="pipeline-stat-value">{dash(funnelCount("Applied"))}</div>
                  <div className="pipeline-stat-label">Applied</div>
                </div>
                <div className="pipeline-stat stat-interview">
                  <div className="pipeline-stat-value">{dash(funnelCount("Interview"))}</div>
                  <div className="pipeline-stat-label">Interview</div>
                </div>
                <div className="pipeline-stat stat-shortlisted">
                  <div className="pipeline-stat-value">{dash(funnelCount("Shortlisted"))}</div>
                  <div className="pipeline-stat-label">Shortlisted</div>
                </div>
                <div className="pipeline-stat stat-offer">
                  <div className="pipeline-stat-value">{dash(offerHiredTotal)}</div>
                  <div className="pipeline-stat-label">Offer/Hired</div>
                </div>
              </div>

              <div className="funnel-title">Conversion Funnel</div>

              <div className="funnel-list">
                {FUNNEL_ROWS.map(([stage, cls]) => {
                  const count = funnelCount(stage);
                  const pct = count && funnelMax ? Math.max((count / funnelMax) * 100, 3) : 0;
                  return (
                    <div className="funnel-row" key={stage}>
                      <span className="funnel-label">{stage}</span>
                      <div className="funnel-track">
                        <div className={`funnel-bar ${cls}`} style={{ width: `${pct}%` }}></div>
                      </div>
                      <span className="funnel-count">{dash(count)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="side-cards">
            {/* This Week's Interviews */}
            <div className="card">
              <div className="card-header">
                <h4>
                  <i
                    className="fa-solid fa-calendar-week"
                    style={{ color: "var(--blue)", fontSize: ".82rem", marginRight: ".35rem" }}
                  ></i>
                  This Week's Interviews
                </h4>
                <span className="week-total">{upcoming.length ? `${upcoming.length} total` : "-"}</span>
              </div>

              <div className="week-days">
                {weekDays.map((d) => {
                  const isToday = d === todayIdx;
                  const cls = `day-box ${isToday ? "day-today" : ""} ${d === 6 && !isToday ? "day-sat" : ""}`;
                  return (
                    <div className={cls.trim()} key={d}>
                      <div className="day-value">{upcoming.length ? weekCounts[d] : "-"}</div>
                      <div className="day-label">{isToday ? "Today" : DAY_LABELS[d]}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Offers in flight */}
            <div className="card">
              <div className="card-header">
                <h4>
                  <i
                    className="fa-solid fa-file-contract"
                    style={{ color: "var(--green)", fontSize: ".82rem", marginRight: ".35rem" }}
                  ></i>
                  Offers in Flight
                </h4>
              </div>

              <div className="offers-body">
                {offers.length === 0 ? (
                  <EmptyDash />
                ) : (
                  offers.map((o, i) => {
                    const isSent = o.OfferStatus === "Sent";
                    const isLate = isSent && o.DaysSinceSent > 7;
                    return (
                      <div
                        className={`offer-item ${i % 2 === 0 ? "offer-neha" : "offer-meera"}`}
                        key={o.Guid || i}
                      >
                        <div className={`offer-avatar ${i % 2 === 0 ? "avatar-neha" : "avatar-meera"}`}>
                          {initials(o.CandidateName)}
                        </div>

                        <div className="offer-info">
                          <div className="offer-name">{dash(o.CandidateName)}</div>
                          <div className="offer-detail">
                            {dash(o.JobTitle)} ·{" "}
                            {o.DaysSinceSent !== undefined && o.DaysSinceSent !== null
                              ? `Sent ${o.DaysSinceSent} days ago`
                              : "-"}
                          </div>
                        </div>

                        <span className={`offer-status ${isLate ? "status-overdue" : "status-awaiting"}`}>
                          {isLate ? "Overdue" : dash(o.OfferStatus)}
                        </span>

                        {isSent && (
                          <button
                            className={`btn-ghost btn-xs ${isLate ? "reminder-danger" : ""}`}
                            title="Send reminder"
                          >
                            <i className="fa-solid fa-bell"></i>
                          </button>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="activity-layout">
          {/* Recent Activity (no data source in API yet) */}
          <div className="card">
            <div className="activity-header">
              <h4>
                <i className="fa-solid fa-clock-rotate-left activity-icon"></i>
                Recent Activity
              </h4>
              <button className="btn btn-outline btn-xs">View All</button>
            </div>

            <div className="activity-feed">
              <div className="feed-column">
                <EmptyDash />
              </div>
            </div>
          </div>

          <div className="quick-actions">
            <div className="card">
              <div className="quick-header">
                <h4>Quick Actions</h4>
              </div>

              <div className="quick-body">
                <div className="quick-buttons">
                  <Link to="/Recruitment/Candidate-CVUpload" className="btn btn-primary btn-sm quick-btn">
                    <i className="fa-solid fa-cloud-arrow-up"></i>
                    Upload CV / Add Candidate
                  </Link>
                  <Link to="/Recruitment/Reimbursement-Job/Create" className="btn btn-outline btn-sm quick-btn">
                    <i className="fa-solid fa-briefcase"></i>
                    Create New Job
                  </Link>
                  <Link to="/Recruitment/ScheduleInterview" className="btn btn-outline btn-sm quick-btn">
                    <i className="fa-solid fa-calendar-plus"></i>
                    Schedule Interview
                  </Link>
                  <Link to="/Recruitment/Talent-Request-Form" className="btn btn-outline btn-sm quick-btn">
                    <i className="fa-solid fa-clipboard-list"></i>
                    Raise Talent Request
                  </Link>
                  <Link to="/Recruitment/RecruitmentLinkCandidate" className="btn btn-outline btn-sm quick-btn">
                    <i className="fa-solid fa-link"></i>
                    Link Candidate to Job
                  </Link>
                </div>
              </div>
            </div>

            {/* This Month */}
            <div className="card">
              <div className="month-header">
                <h4>This Month</h4>
              </div>

              <div className="month-body">
                <div className="month-row">
                  <span className="month-label">Candidates added</span>
                  <strong>{dash(month.CandidatesAdded)}</strong>
                </div>
                <div className="month-row">
                  <span className="month-label">Interviews scheduled</span>
                  <strong>{dash(month.InterviewsScheduled)}</strong>
                </div>
                <div className="month-row">
                  <span className="month-label">Offers given</span>
                  <strong>{dash(month.OffersGiven)}</strong>
                </div>
                <div className="month-row">
                  <span className="month-label">Hires confirmed</span>
                  <strong className="month-success">{dash(month.HiresConfirmed)}</strong>
                </div>
                <div className="month-row">
                  <span className="month-label">Avg. days to offer</span>
                  <strong>{dash(month.AvgDaysToOffer)}</strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showModals.feedback && (
        <div
          className="modal-overlay open"
          id="feedbackModal"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeModal("feedback");
          }}
        >
          <div className="modal-box wide">
            <div className="modal-head">
              <div className="modal-icon feedback-modal-icon">
                <i className="fa-solid fa-star"></i>
              </div>
              <div className="feedback-modal-title-wrap">
                <div className="modal-title"> Interview Feedback </div>
                <div className="modal-sub">
                  {dash(selectedInterview?.RoundName)} · {dash(selectedInterview?.CandidateName)}
                </div>
              </div>
              <button className="modal-close-btn" onClick={() => closeModal("feedback")}>
                <i className="fa-solid fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div id="fbAspects">
                {[
                  { id: "r1", title: "Technical Skills", placeholder: "Technical knowledge, depth of understanding…" },
                  { id: "r2", title: "Communication", placeholder: "Clarity, articulation, listening…" },
                  { id: "r3", title: "Problem Solving", placeholder: "Approach, reasoning, edge case handling…" },
                  { id: "r4", title: "Attitude & Culture Fit", placeholder: "Team fit, enthusiasm, growth mindset…" },
                ].map((aspect) => (
                  <div className="feedback-aspect" key={aspect.id}>
                    <div className="feedback-aspect-header">
                      <label className="feedback-aspect-label">{aspect.title}</label>
                      <div className="feedback-rating">
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((rating) => (
                          <button
                            key={rating}
                            type="button"
                            className={`feedback-rating-btn ${ratings[aspect.id] >= rating ? "feedback-rating-active" : ""}`}
                            onClick={() => setRating(aspect.id, rating)}
                          >
                            {rating}
                          </button>
                        ))}
                      </div>
                    </div>
                    <textarea className="og-textarea" rows="2" placeholder={aspect.placeholder} />
                  </div>
                ))}
              </div>
              <div className="feedback-recommendation">
                <label className="feedback-recommendation-title">Overall Recommendation</label>
                <div className="feedback-recommendation-options">
                  <label className="feedback-recommendation-option">
                    <input type="radio" name="rec" className="feedback-radio-proceed" />
                    <span className="feedback-proceed"> Proceed </span>
                  </label>
                  <label className="feedback-recommendation-option">
                    <input type="radio" name="rec" className="feedback-radio-maybe" />
                    <span className="feedback-maybe"> Maybe </span>
                  </label>
                  <label className="feedback-recommendation-option">
                    <input type="radio" name="rec" className="feedback-radio-reject" />
                    <span className="feedback-reject"> Reject </span>
                  </label>
                </div>
              </div>
            </div>
            <div className="modal-foot">
              <button className="btn btn-secondary btn-sm" onClick={() => closeModal("feedback")}>
                Cancel
              </button>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => {
                  closeModal("feedback");
                  toast("Feedback submitted successfully");
                }}
              >
                <i className="fa-solid fa-check"></i> Submit Feedback
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default RecruitmentDashboard;